const STORAGE_KEY_PREFIX = 'transaction-note-overrides:v1:';
const scopedTransactionNotes = new Map();

const hasWindow = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const normalizeScopeKey = (scopeKey) => {
  if (!scopeKey) return null;
  const normalized = String(scopeKey).trim();
  return normalized || null;
};

const buildStorageKey = (scopeKey) => {
  const normalizedScopeKey = normalizeScopeKey(scopeKey);
  if (!normalizedScopeKey) return null;
  return `${STORAGE_KEY_PREFIX}${normalizedScopeKey}`;
};

const normalizeStoredNotes = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  return Object.entries(value).reduce((accumulator, [transactionId, note]) => {
    accumulator[String(transactionId)] = typeof note === 'string' ? note.slice(0, 30) : '';
    return accumulator;
  }, {});
};

const clearLegacyStorageForScope = (scopeKey) => {
  const storageKey = buildStorageKey(scopeKey);
  if (hasWindow() && storageKey) {
    window.localStorage.removeItem(storageKey);
  }
};

const load = (scopeKey) => {
  const normalizedScopeKey = normalizeScopeKey(scopeKey);
  if (!normalizedScopeKey) return {};

  clearLegacyStorageForScope(normalizedScopeKey);
  return { ...(scopedTransactionNotes.get(normalizedScopeKey) || {}) };
};

const save = (scopeKey, notesByTransactionId) => {
  const normalizedScopeKey = normalizeScopeKey(scopeKey);
  if (!normalizedScopeKey) return false;

  scopedTransactionNotes.set(normalizedScopeKey, normalizeStoredNotes(notesByTransactionId));
  clearLegacyStorageForScope(normalizedScopeKey);
  return false;
};

export const buildTransactionNotesScopeKey = (userId, householdId) => {
  if (!userId || !householdId) return null;
  return `${userId}:${householdId}`;
};

export const getAllPersistedTransactionNotes = (scopeKey) => load(scopeKey);

export const clearPersistedTransactionNotesForScope = (scopeKey) => {
  const storageKey = buildStorageKey(scopeKey);
  const normalizedScopeKey = normalizeScopeKey(scopeKey);
  if (normalizedScopeKey) {
    scopedTransactionNotes.delete(normalizedScopeKey);
  }
  if (hasWindow() && storageKey) {
    window.localStorage.removeItem(storageKey);
  }
};

export const clearAllPersistedTransactionNotes = () => {
  scopedTransactionNotes.clear();
  if (!hasWindow()) return;

  Object.keys(window.localStorage)
    .filter((key) => key.startsWith(STORAGE_KEY_PREFIX))
    .forEach((key) => window.localStorage.removeItem(key));
};

export const savePersistedTransactionNote = (transactionId, note, scopeKey) => {
  const currentNotes = load(scopeKey);
  const normalizedTransactionId = String(transactionId);
  const trimmedNote = typeof note === 'string' ? note.trim().slice(0, 30) : '';
  const nextNotes = {
    ...currentNotes,
    [normalizedTransactionId]: trimmedNote,
  };

  const persisted = save(scopeKey, nextNotes);
  return {
    note: trimmedNote,
    persisted,
  };
};
